import ConfirmationDialog from "@components/ConfirmationDialog";
import CustomSnackBar from "@components/CustomSnackBar";
import StyledTableCell from "@components/Table/TableCell";
import StyledTableRow from "@components/Table/TableRow";
import { TableRowsSkeleton } from "@components/Table/TableRowsSkeleton";
import {
  ROUTING_ADMIN_EDIT_CLIENT,
  ROUTING_ADMIN_NEW_CLIENT
} from "@constants/adminPanelRouting";
import {
  INITIAL_SNACKBAR_STATE,
  ISnackbarAlert,
  setInitialSnackbarState,
  setSuccessAlert
} from "@constants/alerts";
import { BUTTON_PRIMARY_ICON_FONT_SIZE } from "@constants/button";
import ROUTES from "@constants/routing";
import {
  TABLE_BODY_CELL_FONT_SIZE,
  TABLE_HEADER_CELL_FONT_SIZE
} from "@constants/table";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PaletteIcon from "@mui/icons-material/Palette";
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip
} from "@mui/material";
import { useClients, useDeleteClient } from "@services/api";
import { ClientShort } from "@services/api/types";
import { useQueryClient } from "@tanstack/react-query";
import { defaultLabelDisplayedRows } from "@utils/table";
import AdminPanelNavBar from "@views/ViewAdminPanel/components/AdminPanelNavBar";
import dayjs from "dayjs";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { columns } from "./ClientTableTypes";

const TABLE_PALETTE_ICON_FONT_SIZE = "2.0rem";

const ViewHome = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<ClientShort | null>(
    null
  );
  const [snackbarAlert, setSnackbarAlert] = useState<ISnackbarAlert>({
    ...INITIAL_SNACKBAR_STATE
  });

  const mutationDeleteClient = useDeleteClient();
  const queryClient = useQueryClient();

  const { data: clients, isFetching: isLoading } = useClients();

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleNewClientClick = () => {
    navigate(`${ROUTES.ADMIN_PANEL}${ROUTING_ADMIN_NEW_CLIENT}`);
  };

  const handleUpdateClient = (clientId: number) => {
    navigate(
      `${ROUTES.ADMIN_PANEL}${ROUTING_ADMIN_EDIT_CLIENT.replace(":id", clientId.toString())}`
    );
  };

  const handleDeleteClient = useCallback(
    (clientId: number) => {
      mutationDeleteClient.mutate(
        {
          client_id: clientId.toString()
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: ["clients"]
            });
            queryClient.refetchQueries({
              queryKey: ["clients"]
            });
            setOpenConfirmationDialog(false);
            setClientToDelete(null);
            setSnackbarAlert(
              setSuccessAlert(t("admin.clients.delete_client_success"))
            );
          },
          onError: () => {
            setOpenConfirmationDialog(false);
            setClientToDelete(null);
          }
        }
      );
    },
    [mutationDeleteClient, queryClient]
  );

  return (
    <Box className="w-full p-4">
      <AdminPanelNavBar activeTab="clients" />
      <Box className="justify-center w-full p-2 md:p-16">
        <Box className="m-4">
          <Tooltip title={t("common.newClient")}>
            <IconButton
              color="info"
              onClick={handleNewClientClick}
              disabled={isLoading}
              style={{ backgroundColor: "white" }}
            >
              <AddBusinessIcon
                sx={{ fontSize: BUTTON_PRIMARY_ICON_FONT_SIZE }}
              />
            </IconButton>
          </Tooltip>
        </Box>
        <Paper className="w-full m-4">
          <TableContainer key="clients-table-container">
            <Table aria-label="collapsible table" key="clients-table">
              <TableHead>
                <TableRow>
                  <StyledTableCell
                    key="collapse-column"
                    style={{ width: 5, borderRight: "1px solid #caccce" }}
                  ></StyledTableCell>
                  {columns.map((column, index) => (
                    <StyledTableCell
                      key={column.id}
                      align={column.align}
                      style={{
                        minWidth: column.minWidth,
                        borderRight:
                          index === columns.length - 1
                            ? "none"
                            : "1px solid #caccce",
                        fontSize: TABLE_HEADER_CELL_FONT_SIZE
                      }}
                    >
                      {t(`newClient.${column.label}`)}
                    </StyledTableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRowsSkeleton
                    id="clients-table-rows-skeleton"
                    numberOfRows={rowsPerPage}
                    columns={columns.length + 1}
                  />
                ) : (
                  (clients || [])
                    .sort((a, b) =>
                      dayjs(b.created_at, "yyyy-MM-dd").diff(
                        dayjs(a.created_at, "yyyy-MM-dd")
                      )
                    )
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => (
                      <StyledTableRow
                        sx={{ "& > *": { borderBottom: "unset" } }}
                        key={row.id}
                      >
                        <TableCell component="th" scope="row" align="center">
                          <Box className="flex justify-center gap-2">
                            <Tooltip title={t("common.edit")}>
                              <IconButton
                                color="info"
                                onClick={() => handleUpdateClient(row.id)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t("common.delete")}>
                              <IconButton
                                color="error"
                                onClick={() => {
                                  setOpenConfirmationDialog(true);
                                  setClientToDelete(row);
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell
                          component="th"
                          scope="row"
                          align="center"
                          style={{ fontSize: TABLE_BODY_CELL_FONT_SIZE }}
                        >
                          {row.name}
                        </TableCell>
                        <TableCell
                          component="th"
                          scope="row"
                          align="center"
                          style={{ fontSize: TABLE_BODY_CELL_FONT_SIZE }}
                        >
                          {row.label || ""}
                        </TableCell>
                        <TableCell
                          component="th"
                          scope="row"
                          align="center"
                          style={{ fontSize: TABLE_BODY_CELL_FONT_SIZE }}
                        >
                          {row.kpi_label}
                        </TableCell>
                        <TableCell component="th" scope="row" align="center">
                          <Tooltip title={row.base_color}>
                            <PaletteIcon
                              style={{
                                color: row.base_color,
                                fontSize: TABLE_PALETTE_ICON_FONT_SIZE
                              }}
                            />
                          </Tooltip>
                        </TableCell>
                      </StyledTableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 20]}
            labelRowsPerPage={t("table.rows_per_page")}
            labelDisplayedRows={defaultLabelDisplayedRows}
            component="div"
            count={(clients || []).length ?? 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
        <ConfirmationDialog
          open={openConfirmationDialog}
          onClose={() => setOpenConfirmationDialog(false)}
          onConfirm={() => handleDeleteClient(clientToDelete?.id || -1)}
          title={t("admin.clients.delete_client", {
            name: clientToDelete?.name || ""
          })}
        />
        <CustomSnackBar
          id={snackbarAlert.message}
          key={snackbarAlert.message}
          open={snackbarAlert.open}
          message={snackbarAlert.message}
          handleClose={() => setSnackbarAlert(setInitialSnackbarState())}
          autoHideDuration={snackbarAlert.autoHideDuration}
          color={snackbarAlert.severity}
        />
      </Box>
    </Box>
  );
};

export default ViewHome;
